import { useApolloClient, useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

import { evictCache } from '@neo.tax/shared/apollo';
import {
  PayrollProvider,
  SYNC_PAYROLL_PROVIDER,
  SyncPayrollProviderMutation,
  SyncPayrollProviderMutationVariables,
  GET_SYNCED_PROVIDERS,
  GetSyncedProvidersQueryVariables,
} from '@neo.tax/shared/graphql';
import { useCurrentCompanyId, useCurrentCompany, useCurrentUser } from '@neo.tax/shared/hooks';
import { getGustoAuthUrl, sendFSEvent } from '@neo.tax/shared/third-parties';
import { getPathUrl, Path } from '@neo.tax/shared/utils';

import { ConnectProviderStatus } from '../enums';
import { UseProviderConnection } from '../types';
import { useProviderConnectState } from './useProviderConnectState';

export const useSyncPayrollProvider = ({
  fiscalYearId,
  providerName,
}: {
  fiscalYearId: string;
  providerName: PayrollProvider;
}): UseProviderConnection => {
  const [connectStatus, setConnectStatus] = useState<ConnectProviderStatus>(ConnectProviderStatus.NOT_STARTED);
  const [startedPolling, setStartedPolling] = useState(false);
  const companyId = useCurrentCompanyId();
  const { company } = useCurrentCompany();
  const { user } = useCurrentUser();
  const client = useApolloClient();
  const { loading, payrollToken, startPolling, stopPolling, synced, tokenRegistered } = useProviderConnectState({
    fiscalYearId,
    providerName,
    providerType: 'payroll',
  });
  const [syncPayrollProvider, { data }] = useMutation<
    SyncPayrollProviderMutation,
    SyncPayrollProviderMutationVariables
  >(SYNC_PAYROLL_PROVIDER);

  const syncPayroll = useCallback(async () => {
    if (!payrollToken) {
      return;
    }
    setConnectStatus(ConnectProviderStatus.SYNCING);
    try {
      await syncPayrollProvider({
        variables: {
          fiscalYearId,
          payrollProvider: providerName,
        },
        awaitRefetchQueries: true,
        update: (cache) => {
          //invalidate getPayrollQres and getPayrollQreSummary cache
          evictCache(cache, ['getPayrollQres', 'getPayrollQreSummary']);
        },
        refetchQueries: [
          {
            query: GET_SYNCED_PROVIDERS,
            variables: {
              fiscalYearId,
            } satisfies GetSyncedProvidersQueryVariables,
          },
        ],
        onCompleted: (data) => {
          if (data.syncPayrollProvider.errors) {
            setConnectStatus(ConnectProviderStatus.ERROR);
          } else {
            setConnectStatus(ConnectProviderStatus.SUCCESS);
          }
        },
      });
    } catch (err) {
      console.error(err);
      setConnectStatus(ConnectProviderStatus.ERROR);
    }
  }, [payrollToken, syncPayrollProvider, fiscalYearId, providerName]);

  const openPayrollAuthUrl = (): void => {
    // only Gusto is supported as direct link payroll provider
    if (providerName !== PayrollProvider.Gusto) {
      return;
    }
    const authUrl = getGustoAuthUrl({
      userId: user?.id || '',
      companyId,
      successRedirectUrl: getPathUrl({ path: Path.PAYROLL_QRE, companyId, fiscalYearId }),
      errorRedirectUrl: getPathUrl({ path: Path.PAYROLL_QRE, companyId, fiscalYearId }),
    });
    setStartedPolling(true);
    startPolling(1000);
    sendFSEvent('gusto_open', {}, user, company);
    window.open(authUrl, '_blank');
  };

  useEffect(() => {
    if (!startedPolling || !tokenRegistered) {
      return;
    }
    stopPolling();
    setStartedPolling(false);
  }, [client, companyId, startedPolling, stopPolling, tokenRegistered]);

  const connectPayroll = (): void => {
    openPayrollAuthUrl();
  };

  useEffect(() => {
    if (loading || !payrollToken || synced) {
      return;
    }
    if (!company?.businessQuestions.gustoCompanyId) {
      return;
    }

    syncPayroll();
  }, [company?.businessQuestions.gustoCompanyId, payrollToken, providerName, loading, synced, syncPayroll]);

  return {
    connect: connectPayroll,
    connectStatus,
    errors: data?.syncPayrollProvider.errors,
  };
};
